/**
 * Typed bag of `/internal/admin/*` calls.
 *
 * The shapes are imported from `@sill/types/api` (admin-api.ts) so the
 * SPA and the API agree on every wire field. Snake_case on the wire
 * per CLAUDE.md §5 / spec §5 — no camelCase conversion at the
 * boundary; the admin renders snake_case directly.
 *
 * Each method is a thin wrapper that names the path + method + body +
 * query, then delegates to `HttpClient.request`. The factory takes
 * the HttpClient as a dep so tests can inject a stub.
 */

import type {
  AdminAgentRegistryApproveBody,
  AdminAgentRegistryDecisionResponse,
  AdminAgentRegistryResponse,
  AdminHomeExperimentEventsResponse,
  AdminHomeExperimentPutBody,
  AdminHomeExperimentResponse,
  TrustEngineAdminResponse,
  TrustReadinessResponse,
  SetTrustVerdictFlagRequest,
  AdminTrustDisputesResponse,
  AdminAgentOperatorsResponse,
  AdminAgentOperatorDetailResponse,
  AdminAgentOperatorInviteResponse,
  AdminOperatorReviewAction,
  AdminOperatorAgentReviewAction,
  AdminTrustVerdictFlagsResponse,
  AdminSupportLoginCreateResponse,
  AdminSettingHistoryResponse,
  AdminAccountDetailResponse,
  AdminAccountUsageResponse,
  AdminAccountsListQuery,
  AdminAccountsListResponse,
  AdminAccountUpdateBody,
  AdminAccountUpdateResponse,
  AdminAuthCallbackResponse,
  AdminBillingUpdateBody,
  AdminBillingUpdateResponse,
  AdminEnforcementToggleResponse,
  AdminEntitlementOverridesPutBody,
  AdminEntitlementOverridesPutResponse,
  AdminMeResponse,
  AdminPlanCreateBody,
  AdminPlanCreateResponse,
  AdminPlanDetailResponse,
  AdminAdHocSmokeSkill,
  AdminSmokeRunAdHocResponse,
  AdminSmokeRunDetail,
  AdminSettingsResponse,
  AdminAcpProbeResult,
  AdminAcpProbeRequest,
  AdminUcpCheckoutTestResponse,
  AdminUcpCheckoutTestFinishRequest,
  AdminUcpCheckoutTestRequest,
  AdminDeletionRunListResponse,
  AdminDeletionRunResponse,
  AdminPurgeListResponse,
  AdminJobsResponse,
  AdminLeadsResponse,
  AdminReportLinksBody,
  AdminReportLinksResponse,
  AdminSendReportBody,
  AdminSendReportResult,
  AdminStaffLogResponse,
  AdminStaffListResponse,
  AdminStaffMember,
  AdminPurgeApproveResponse,
  AdminPurgeRejectResponse,
  AdminSmokeRunHeader,
  AdminSmokeRunStartResponse,
  AdminSmokeRunsListResponse,
  // ADR-0255
  AdminProtocolTestProtocol,
  AdminProtocolTestRunDetail,
  AdminProtocolTestRunsListResponse,
  AdminPlanEntitlementsPutBody,
  AdminPlanEntitlementsPutResponse,
  AdminPlanPricesPutBody,
  AdminPlanPricesPutResponse,
  AdminPlanUpdateBody,
  AdminPlanUpdateResponse,
  AdminPlansListResponse,
  AdminSubscriptionAssignBody,
  AdminSubscriptionAssignResponse,
  // ADR-0263 S1 — Plans v2.
  AdminPlanFeaturesPutBody,
  AdminPlanFeaturesPutResponse,
  AdminPlanAddonCreateBody,
  AdminPlanAddonCreateResponse,
  AdminPlanAddonDetailResponse,
  AdminPlanAddonPricesPutBody,
  AdminPlanAddonPricesPutResponse,
  AdminPlanAddonUpdateBody,
  AdminPlanAddonUpdateResponse,
  AdminPlanAddonsListResponse,
  AdminAccountFeaturesInUseResponse,
  AdminAccountSitePlansResponse,
  AdminSitePlanAssignBody,
  AdminSitePlanAssignResponse,
  AdminAccountAddonsResponse,
  AdminAccountAddonUpdateBody,
  AdminAccountAddonUpdateResponse,
  AdminAccountSiteUsageResponse,
  AdminBillingSyncV2Response,
  AdminPricingPreviewResponse,
} from '@sill/types/api';
import type {
  AdminChurnRiskResponse,
  AdminCiRunsResponse,
  AdminEscalateBody,
  AdminEscalateResponse,
  AdminHealthReport,
  AdminMetricsCohorts,
  AdminMetricsOverview,
  AdminMetricsAssistants,
} from '@sill/types';
import type {
  AdminInstallFleetPlan,
  AdminInstallFleetRunBody,
  AdminInstallFleetRunDetail,
  AdminInstallFleetRunResult,
  AdminInstallFleetRunsResponse,
} from '@sill/types/api';
import type {
  AdminVerifiedCrawlerApproveRequest,
  AdminVerifiedCrawlerApproveResponse,
  AdminVerifiedCrawlerFetchResponse,
  AdminVerifiedCrawlerRejectRequest,
  AdminVerifiedCrawlerRejectResponse,
  AdminVerifiedCrawlerReviewResponse,
} from '@sill/types/api';
import type {
  AdminAttackPathDecisionOverrideResponse,
  AdminAttackPathDecisionsResponse,
} from '@sill/types/api';
// Founder request 2026-10-29 — ops2026 Agencies panel.
import type {
  AdminAgenciesListResponse,
  AdminAgencyAddClientBody,
  AdminAgencyAddClientResponse,
  AdminAgencyDetailResponse,
  AdminAgencyMoveClientBody,
  AdminAgencyMoveClientResponse,
  AdminAgencyOfRecordBody,
  AdminAgencyOfRecordResponse,
  AdminAgencyPaysForSitesBody,
  AdminAgencyPaysForSitesResponse,
  AdminAgencyRemoveClientBody,
  AdminAgencyRemoveClientResponse,
} from '@sill/types/api';
// ADR-0263 §8 — self-serve agency-plan requests surface.
import type {
  AdminAgencyRequestDismissResponse,
  AdminAgencyRequestsListResponse,
} from '@sill/types/api';
import type { ReportingRange } from '@sill/types';

import type { HttpClient } from './http-client.js';

export interface AdminClientDeps {
  readonly http: HttpClient;
}

/** ADR-0237 — response shape of
 *  GET  /internal/admin/accounts/:id/features
 *  POST /internal/admin/accounts/:id/features/agency
 *  POST /internal/admin/accounts/:id/features/white-label
 *
 *  The GET returns the current toggle state (defaults to false/false
 *  for an account with no toggle row). The POST returns the same
 *  shape with the newly-written value folded in — the SPA uses that
 *  echo to patch local state without a refetch. */
export interface AdminAccountFeatures {
  readonly agency: boolean;
  readonly white_label: boolean;
}

/** Response shape of `POST /internal/admin/billing/provider-sync` (ADR-0155 D1). */
export interface AdminBillingSyncResponse {
  readonly provider: string;
  readonly synced: ReadonlyArray<{ plan_id: string; interval: string; price_ref: string }>;
  readonly skipped: ReadonlyArray<{ plan_id: string; interval: string }>;
  readonly failed: ReadonlyArray<{ plan_id: string; interval: string; reason: string }>;
}

export interface AdminClient {
  // Auth
  /** POST /internal/admin/auth/request — anti-enumeration: always 200. */
  requestSignIn(email: string): Promise<void>;
  /** POST /internal/admin/auth/callback {token} — server sets the cookie. */
  consumeToken(token: string): Promise<AdminAuthCallbackResponse>;
  /** GET /internal/admin/me — used for the bootstrap auth check. */
  me(opts?: { throwOnUnauthorized?: boolean }): Promise<AdminMeResponse>;
  /** POST /internal/admin/auth/logout — invalidates the staff session. */
  logout(): Promise<void>;

  // Accounts
  listAccounts(query: AdminAccountsListQuery): Promise<AdminAccountsListResponse>;
  getAccount(accountId: string): Promise<AdminAccountDetailResponse>;
  updateAccount(
    accountId: string,
    body: AdminAccountUpdateBody,
  ): Promise<AdminAccountUpdateResponse>;
  updateBilling(
    accountId: string,
    body: AdminBillingUpdateBody,
  ): Promise<AdminBillingUpdateResponse>;
  /**
   * DELETE /internal/admin/accounts/:account_id with body
   * `{ confirm: '<primary_domain or account_id>' }`. The server returns
   * 409 `{ error: 'account_has_audit_history' }` if the account has any
   * audit records — in that case the admin should suspend instead.
   */
  deleteAccount(accountId: string, confirm: string): Promise<void>;
  /**
   * DELETE /internal/admin/accounts/:account_id/sites/:site_id —
   * soft-delete; safe to call multiple times.
   */
  deleteSite(accountId: string, siteId: string): Promise<void>;

  /**
   * ADR-0216 — mint a one-time staff support-login grant for the
   * target account. Returns the redeem URL (with the raw token in the
   * URL FRAGMENT — never in query — so the token stays out of access
   * logs) + the expires_at + how many prior unused grants were
   * revoked as part of this mint (regenerating cancels the previous).
   * Superadmin only server-side; the SPA hides the button for 'support'.
   */
  /** `allowChanges` generates an edit-capable link (default read-only). */
  createSupportLogin(accountId: string, allowChanges?: boolean): Promise<AdminSupportLoginCreateResponse>;

  // ADR-0237 — account feature toggles (agency, white_label). Reads +
  // writes are superadmin on the server (STAFF_ROUTE_MIN_ROLE='admin');
  // the SPA role-gates to match.
  getAccountFeatures(accountId: string): Promise<AdminAccountFeatures>;
  setAccountAgencyFeature(accountId: string, enabled: boolean): Promise<AdminAccountFeatures>;
  setAccountWhiteLabelFeature(accountId: string, enabled: boolean): Promise<AdminAccountFeatures>;

  // Founder request 2026-10-29 — ops2026 Agencies panel. Admin-only
  // (server also enforces STAFF_ROUTE_MIN_ROLE='admin').
  /** List / search accounts with `agency` feature toggle on. */
  listAgencies(query: { readonly q?: string }): Promise<AdminAgenciesListResponse>;
  /** Managed clients + attributed-signups snapshot for ONE agency. */
  getAgencyDetail(agencyAccountId: string): Promise<AdminAgencyDetailResponse>;
  /** Add a client to an agency (same effect as a client-approved link). */
  addAgencyClient(
    agencyAccountId: string,
    body: AdminAgencyAddClientBody,
  ): Promise<AdminAgencyAddClientResponse>;
  /** Remove a client from an agency (revoked_by_role='ops'). */
  removeAgencyClient(
    agencyAccountId: string,
    linkId: string,
    body: AdminAgencyRemoveClientBody,
  ): Promise<AdminAgencyRemoveClientResponse>;
  /** Move a client to another agency atomically; overrides AoR. */
  moveAgencyClient(
    agencyAccountId: string,
    linkId: string,
    body: AdminAgencyMoveClientBody,
  ): Promise<AdminAgencyMoveClientResponse>;
  /** Set or clear `account.agency_of_record_account_id`. */
  setAgencyOfRecord(
    accountId: string,
    body: AdminAgencyOfRecordBody,
  ): Promise<AdminAgencyOfRecordResponse>;
  /**
   * ADR-0263 §5.3 (S6) — flip the per-link agency-paid switch. Staff
   * admin only; audited server-side in the same tx. Idempotent.
   */
  setAgencyPaysForSites(
    agencyAccountId: string,
    linkId: string,
    body: AdminAgencyPaysForSitesBody,
  ): Promise<AdminAgencyPaysForSitesResponse>;

  // ADR-0263 §8 — pending self-serve agency-plan requests (staff
  // surface mounted above the Agencies search list).
  /** List pending requests for the ops2026 Agencies panel. */
  listAgencyRequests(): Promise<AdminAgencyRequestsListResponse>;
  /** Mark a pending request `dismissed`. Idempotent — a second call
   *  returns `{ ok: true, changed: false }`. */
  dismissAgencyRequest(accountId: string): Promise<AdminAgencyRequestDismissResponse>;

  // Metrics (ADR-0140 P1 metrics slice)
  /**
   * GET /internal/admin/metrics/overview?range=<24h|7d|30d|90d|1y>
   * — KPI snapshot + per-bucket time-series. Cross-account aggregates
   * only; no PII, no per-account identifiers.
   */
  getMetricsOverview(range: ReportingRange): Promise<AdminMetricsOverview>;
  /**
   * GET /internal/admin/metrics/assistants?range=<24h|7d|30d|90d|1y>
   * — ADR-0262: assistant MCP adoption, engagement, reliability and
   * the OAuth funnel. Aggregate-only.
   */
  getMetricsAssistants(range: ReportingRange): Promise<AdminMetricsAssistants>;
  /**
   * GET /internal/admin/metrics/cohorts[?country=<ISO|ZZ|all>] —
   * full per-(cohort_month, month_index) retention grid. Server
   * returns oldest cohort first.
   *
   * `country` is optional; when omitted (or `'all'`) the server returns
   * the ungrouped lifetime grid (back-compat with the pre-geography
   * wire shape). When set to an ISO-3166-1 alpha-2 or the literal
   * `'ZZ'` sentinel, the server returns the same grid filtered to
   * accounts whose modal country is that value. The resolved filter
   * is echoed on `AdminMetricsCohorts.country`.
   */
  getMetricsCohorts(country?: string): Promise<AdminMetricsCohorts>;
  /**
   * GET /internal/admin/metrics/churn-risk — per-account drill-down
   * for the `AdminMetricsChurn.at_risk` headline count. No query
   * params; the server pegs its own NOW + window edges and returns
   * them on the response for honest display. Sorted longest-silent
   * first; capped at the server-side `limit` (today's cap is 500),
   * with `truncated=true` when the cap was hit. Staff-only PII surface
   * (`owner_email`); same bypass path as the accounts list.
   */
  getChurnRisk(): Promise<AdminChurnRiskResponse>;

  // Health (vitals)
  /**
   * GET /internal/admin/health/checks — LIVE health probes (DB
   * reachability, JWKS reachability, rollup vs live recompute
   * reconciliation, audit_record pipeline tells). Each check is
   * independent — a single check's failure NEVER 500s the whole
   * endpoint. Aggregate-only on the wire; no PII.
   */
  getHealthChecks(): Promise<AdminHealthReport>;

  // ADR-0239 — install fleet redeploy (ops-only, admin role gate on
  // the server).
  getInstallFleetPlan(): Promise<AdminInstallFleetPlan>;
  runInstallFleetRedeploy(body: AdminInstallFleetRunBody): Promise<AdminInstallFleetRunResult>;
  getInstallFleetRuns(): Promise<AdminInstallFleetRunsResponse>;
  getInstallFleetRun(run_id: string): Promise<AdminInstallFleetRunDetail>;

  // ADR-0245 §4.4.2 — verified-crawler review surface.
  getVerifiedCrawlerReview(): Promise<AdminVerifiedCrawlerReviewResponse>;
  /** Admin+. Manual-only; writes a pending snapshot, never promotes. */
  fetchVerifiedCrawlers(): Promise<AdminVerifiedCrawlerFetchResponse>;
  /** Owner-only (enforced server-side). Hash-pinned; the UI passes the
   *  exact `snapshot_hash` it rendered against. */
  approveVerifiedCrawlerSnapshot(
    snapshotId: string,
    body: AdminVerifiedCrawlerApproveRequest,
  ): Promise<AdminVerifiedCrawlerApproveResponse>;
  rejectVerifiedCrawlerSnapshot(
    snapshotId: string,
    body: AdminVerifiedCrawlerRejectRequest,
  ): Promise<AdminVerifiedCrawlerRejectResponse>;

  // ADR-0251 — attack-path judgement panel decisions. Admin+ read;
  // owner-only approve / remove overrides.
  getAttackPathDecisions(status?: string): Promise<AdminAttackPathDecisionsResponse>;
  approveAttackPathDecision(additionId: string): Promise<AdminAttackPathDecisionOverrideResponse>;
  removeAttackPathDecision(additionId: string): Promise<AdminAttackPathDecisionOverrideResponse>;

  // QA console (ADR-0140 P1 follow-up — CI runs + escalation)
  /**
   * GET /internal/admin/qa/ci-runs — recent GitHub Actions runs on
   * the repo's main branch (newest-first). GRACEFUL DORMANT: when the
   * read-only CI token isn't set, the route returns 200 with
   * `{ configured: false, runs: [] }` so the UI can show a
   * "Connect GitHub" empty state instead of an error.
   */
  getCiRuns(): Promise<AdminCiRunsResponse>;

  /**
   * POST /internal/admin/qa/escalate — creates one GitHub issue in
   * `siteforbiz/sill` describing a failing health check or CI run.
   *
   * The body is a CLOSED input surface: `kind`, `ref`, `summary`,
   * and the optional `status` / `detail`. There is no free-form issue
   * body — the server constructs the title + body from a fixed
   * template using the (sanitized + truncated) inputs.
   *
   * Surfaces three error branches the UI cares about distinctly:
   *   - HTTP 409 `{ configured:false, error:'github_issues_not_configured' }`
   *     → `ApiError` with kind=`'conflict'`. The UI renders this as
   *     a "GitHub issues aren't configured yet" affordance, NOT as a
   *     hard error toast.
   *   - HTTP 429 → `ApiError` with kind=`'rate_limited'`. The UI
   *     renders a "Rate limit reached (10/hour) — try later" message.
   *   - Anything else → bubbles up the `ApiError` for the standard
   *     error-banner path.
   */
  escalateIssue(body: AdminEscalateBody): Promise<AdminEscalateResponse>;

  // Smoke-runs (ADR-0176)
  /** POST /internal/admin/smoke-runs — start a new run against `account_id`. */
  /** `siteId` pins the run to one site; omitted = server picks the first
   *  live site with catalog data. */
  startSmokeRun(accountId: string, siteId?: string): Promise<AdminSmokeRunStartResponse>;
  /** POST /internal/admin/smoke-runs/:smoke_run_id/advance — server-only
   *  advance one step of the state machine. NO body payload. */
  advanceSmokeRun(smokeRunId: string): Promise<AdminSmokeRunDetail>;
  /** GET /internal/admin/smoke-runs/:smoke_run_id — poll one run. */
  getSmokeRun(smokeRunId: string): Promise<AdminSmokeRunDetail>;
  /** GET /internal/admin/smoke-runs[?account_id=...] — list runs. */
  listSmokeRuns(query?: {
    account_id?: string;
    status?: string;
    limit?: number;
  }): Promise<AdminSmokeRunsListResponse>;
  /** POST /internal/admin/smoke-runs/:smoke_run_id/cancel — mark cancelled. */
  cancelSmokeRun(smokeRunId: string): Promise<AdminSmokeRunDetail>;
  /**
   * POST /internal/admin/smoke-runs/ad-hoc (ADR-0176 addendum
   * 2026-07-10) — start a single-skill run against the account +
   * site. `skill` is restricted at the TS level to the three
   * non-transactional skills (browse_catalog / check_availability
   * / recommend); the server enforces the SAME allowlist and 400s
   * any other value with `invalid_ad_hoc_skill`. Returns a
   * TERMINAL header (`'completed'` or `'aborted'`) — the whole
   * lifecycle fits in one request.
   */
  startAdHocSmokeRun(
    accountId: string,
    siteId: string,
    skill: AdminAdHocSmokeSkill,
  ): Promise<AdminSmokeRunAdHocResponse>;

  // Protocol Tests (ADR-0255)
  /** POST /internal/admin/protocol-tests — start a run (completes
   *  in-request; the response IS the fully populated detail). */
  startProtocolTest(args: {
    site_id?: string;
    domain?: string;
    protocol: AdminProtocolTestProtocol;
    cart_item_id?: string;
  }): Promise<AdminProtocolTestRunDetail>;
  /** GET /internal/admin/protocol-tests/:run_id */
  getProtocolTest(runId: string): Promise<AdminProtocolTestRunDetail>;
  /**
   * POST /internal/admin/protocol-tests/:run_id/pay-held-order —
   * ADR-0253 2026-10-27. Resubmit the escalated AP2 PaymentMandate
   * against the merchant-approved hold. Returns the new (child) run
   * detail. Refuses `pay_held_order_live_mode_no_charge` on a live-
   * mode account; refuses `pay_held_order_ap2_holder_key_unset` when
   * `SILL_OPS_AP2_HOLDER_PRIVATE_JWK` is not deployed; refuses
   * `pay_held_order_not_escalated` when the source run never
   * escalated.
   */
  payHeldOrder(runId: string): Promise<AdminProtocolTestRunDetail>;
  /** GET /internal/admin/protocol-tests */
  listProtocolTests(query?: {
    account_id?: string;
    site_id?: string;
    protocol?: AdminProtocolTestProtocol;
    limit?: number;
  }): Promise<AdminProtocolTestRunsListResponse>;

  // Plans (ADR-0140 P3 / Phase A — catalog + assignment, INERT)
  /**
   * GET /internal/admin/plans — list every plan with its prices +
   * entitlements bundled per row, so the staff console renders the
   * catalog in one round-trip.
   */
  getPlans(): Promise<AdminPlansListResponse>;
  /** GET /internal/admin/plans/:id — single-plan detail (plan + prices + entitlements). */
  getPlan(planId: string): Promise<AdminPlanDetailResponse>;
  /** POST /internal/admin/plans — server mints `plan_id`; status defaults to draft. */
  createPlan(body: AdminPlanCreateBody): Promise<AdminPlanCreateResponse>;
  /** PATCH /internal/admin/plans/:id — partial update. Sending status='archived' archives. */
  updatePlan(
    planId: string,
    body: AdminPlanUpdateBody,
  ): Promise<AdminPlanUpdateResponse>;
  /** PUT /internal/admin/plans/:id/prices — REPLACE semantics (empty array clears). */
  putPlanPrices(
    planId: string,
    body: AdminPlanPricesPutBody,
  ): Promise<AdminPlanPricesPutResponse>;
  /** PUT /internal/admin/plans/:id/entitlements — REPLACE semantics. */
  putPlanEntitlements(
    planId: string,
    body: AdminPlanEntitlementsPutBody,
  ): Promise<AdminPlanEntitlementsPutResponse>;
  /**
   * POST /internal/admin/billing/provider-sync (ADR-0155 D1) — for every
   * active PAID plan/interval without a provider product/price, create it
   * at the provider (Stripe test-mode until the live gate flips) and record
   * it in `plan_provider_ref` so the plan becomes purchasable at checkout.
   * Idempotent; skips $0 tiers and already-synced prices.
   */
  syncBillingProviders(): Promise<AdminBillingSyncResponse>;
  /**
   * POST /internal/admin/billing/provider-sync-v2 (ADR-0263 S5) — for
   * every active v2 (per-site) plan/add-on + interval without a
   * provider product/price, create it at the provider and record it in
   * `plan_provider_ref` / `plan_addon_provider_ref`. Idempotent;
   * complements {@link syncBillingProviders}, which only covers the
   * legacy v1 catalog.
   */
  syncBillingProvidersV2(): Promise<AdminBillingSyncV2Response>;
  /**
   * GET /internal/admin/pricing-preview (ADR-0263) — staff preview of
   * the public pricing page. Same shape as `GET /v1/public/pricing`
   * plus draft plans / add-ons. Read-only.
   */
  getPricingPreview(): Promise<AdminPricingPreviewResponse>;

  // Subscriptions + entitlement overrides (per-account)
  /**
   * PUT /internal/admin/accounts/:account_id/subscription — assign a
   * subscription to the account. Surfaces `ApiError` with
   * `kind='conflict'` (HTTP 409) when the chosen plan is archived; the
   * UI re-prompts the staff member with an "active plans only" hint.
   */
  assignSubscription(
    accountId: string,
    body: AdminSubscriptionAssignBody,
  ): Promise<AdminSubscriptionAssignResponse>;
  /** PUT /internal/admin/accounts/:account_id/entitlement-overrides — REPLACE semantics. */
  putEntitlementOverrides(
    accountId: string,
    body: AdminEntitlementOverridesPutBody,
  ): Promise<AdminEntitlementOverridesPutResponse>;

  /**
   * PUT /internal/admin/accounts/:account_id/enforcement — flip the
   * per-account master enforcement switch (ADR-0143 §8 / Phase C.1).
   *
   * Strict closed boolean — the server upserts the `account_billing`
   * shell row on first flip, so brand-new accounts (with no billing
   * row yet) enroll in one round-trip. Response echoes the post-flip
   * `enforcement_enabled` value so the SPA can update local state
   * without a follow-up GET.
   *
   * Audited server-side as `staff_audit_log` 'enforcement_toggle'.
   * Phase C.1 ships INERT: nothing reads this to block requests yet
   * — the value is toggleable + readable only; hard caps land per
   * dimension in C.2/C.3 (sites + red-team first).
   */
  setEnforcement(
    accountId: string,
    enabled: boolean,
  ): Promise<AdminEnforcementToggleResponse>;

  /**
   * GET /internal/admin/accounts/:account_id/usage — Phase-B visibility.
   *
   * READ-ONLY: returns the current consumed-quantity per dimension
   * alongside the EFFECTIVE limit (override → plan → null). The figures
   * are NOT cached — the server runs four bounded SQL counts per
   * request. NO enforcement: limits aren't applied yet (Phase C).
   *
   * The response set is ALWAYS complete (one row per `AdminUsageDimension`
   * in stable enum order); the staff console renders all four rows
   * unconditionally.
   */
  getAccountUsage(accountId: string): Promise<AdminAccountUsageResponse>;

  /** GET /internal/admin/settings — ops2026 operational toggles. */
  listSettings(): Promise<AdminSettingsResponse>;
  /** GET /internal/admin/settings/history — paginated + sortable
   *  settings change log (staff-audit trail). */
  getSettingsHistory(params: {
    limit: number;
    offset: number;
    sort: 'at' | 'key' | 'staff';
    dir: 'asc' | 'desc';
  }): Promise<AdminSettingHistoryResponse>;
  /** PUT /internal/admin/settings/:key — set a toggle or an int-valued
   *  setting (superadmin). ADR-0185 §2.4: value type must match the key. */
  setSetting(key: string, value: boolean | number): Promise<AdminSettingsResponse>;

  /** POST /internal/admin/acp-connect-probe — ADR-0130 slice 0. Runs the
   *  ACP↔Stripe-Connect SPT compatibility probe (test-mode, superadmin)
   *  and returns a GO / NO-GO verdict. */
  runAcpProbe(body?: AdminAcpProbeRequest): Promise<AdminAcpProbeResult>;

  /**
   * POST /internal/admin/accounts/:account_id/sites/:site_id/ucp-checkout-test
   * — the ops2026 UCP checkout test runner. Issues real Stripe TEST
   * charges + refunds against the merchant's connected account; staff
   * admin+ (403 for support). Rate-limited server-side at 6/hour/staff.
   */
  runUcpCheckoutTest(
    accountId: string,
    siteId: string,
    body?: AdminUcpCheckoutTestRequest,
  ): Promise<AdminUcpCheckoutTestResponse>;

  /** POST …/ucp-checkout-test/finish — second complete + refund for a
   *  held test the merchant approved in Held orders. 409 while still held. */
  finishUcpCheckoutTest(
    accountId: string,
    siteId: string,
    body: AdminUcpCheckoutTestFinishRequest,
  ): Promise<AdminUcpCheckoutTestResponse>;

  /** GET /internal/admin/deletion-runs — account-deletion approvals. */
  listDeletionRuns(): Promise<AdminDeletionRunListResponse>;
  /** POST .../deletion-runs/:id/approve — approve + execute (superadmin). */
  approveDeletionRun(id: string): Promise<AdminDeletionRunResponse>;
  /** POST .../deletion-runs/:id/reject — reject with reason. */
  rejectDeletionRun(id: string, reason: string): Promise<AdminDeletionRunResponse>;
  /** GET /internal/admin/jobs — background job health (job_run_state). */
  listJobs(): Promise<AdminJobsResponse>;
  /** Ops staff management (2026-09-25). */
  listStaff(): Promise<AdminStaffListResponse>;
  requestStaff(body: { email: string; name?: string; role: 'admin' | 'support' | 'viewer' }): Promise<{ staff: AdminStaffMember | null }>;
  /** Owner: email a 6-digit code for one staff action. */
  staffStepUp(body: { action: 'approve' | 'change_role' | 'suspend' | 'reactivate'; staff_id: string; role?: string }): Promise<{ sent_to: string; expires_at: string }>;
  staffAction(staffId: string, action: 'approve' | 'role' | 'suspend' | 'reactivate' | 'deny', body: { code?: string; role?: string }): Promise<{ staff: AdminStaffMember | null }>;
  /** GET /internal/admin/staff-log — staff audit trail (superadmin). */
  listStaffLog(q: { action?: string; q?: string; limit?: number; offset?: number }): Promise<AdminStaffLogResponse>;
  /** GET /internal/admin/leads — captured emails + scanned domains. */
  listLeads(q: { status?: 'all' | 'claimed' | 'unclaimed'; q?: string; limit?: number; offset?: number; kind?: 'leads' | 'domains' }): Promise<AdminLeadsResponse>;
  /** POST /internal/admin/leads/delete — superadmin hard delete. */
  deleteLeads(body: { leads?: { email: string; domain: string | null }[]; domains?: string[] }): Promise<{ lead_rows_deleted: number; scan_rows_deleted: number }>;
  /** POST /internal/admin/leads/send-report — scan + email the report. */
  sendLeadReport(body: AdminSendReportBody): Promise<AdminSendReportResult>;
  /** POST /internal/admin/leads/report-links — shareable links, ≤ 25 domains. */
  createReportLinks(body: AdminReportLinksBody): Promise<AdminReportLinksResponse>;
  /** GET /internal/admin/purge-runs — latest purge proposals (ADR-0180 2b). */
  listPurgeRuns(): Promise<AdminPurgeListResponse>;
  /** POST /internal/admin/purge-runs/:id/approve — approve + execute the
   *  frozen-cutoff sweep. 409 `already_decided` on a decided run. */
  approvePurgeRun(purgeRunId: string): Promise<AdminPurgeApproveResponse>;
  /** POST /internal/admin/purge-runs/:id/reject — reject with a reason. */
  rejectPurgeRun(
    purgeRunId: string,
    reason: string,
  ): Promise<AdminPurgeRejectResponse>;

  /** ADR-0229 — GET /internal/admin/agent-registry (admin). */
  getAgentRegistry(): Promise<AdminAgentRegistryResponse>;
  /** ADR-0229 — approve a candidate (writes AGENT_REGISTRY KV). */
  approveAgentProposal(
    proposalId: string,
    body: AdminAgentRegistryApproveBody,
  ): Promise<AdminAgentRegistryDecisionResponse>;
  /** ADR-0229 — reject a proposal with a reason. */
  rejectAgentProposal(proposalId: string, reason: string): Promise<AdminAgentRegistryDecisionResponse>;
  /** ADR-0229 — snooze a proposal for 7 / 30 / 90 days. */
  snoozeAgentProposal(proposalId: string, days: 7 | 30 | 90): Promise<AdminAgentRegistryDecisionResponse>;
  /** ADR-0230 — revoke an approved identity (removes its edge registry entry). */
  revokeAgentProposal(proposalId: string, reason: string): Promise<AdminAgentRegistryDecisionResponse>;
  /** ADR-0231 — GET /internal/admin/trust-engine (admin, read-only shadow panel). */
  getTrustEngine(opts?: { readonly include_acked?: boolean }): Promise<TrustEngineAdminResponse>;
  /** ADR-0236 — go-live readiness per site / agent. */
  getTrustReadiness(): Promise<TrustReadinessResponse>;
  /** ADR-0236 — acknowledge a key-change alert (note required; staff-audited). */
  ackTrustKeyAlert(alertId: number, note: string): Promise<{ ok: true; alert_id: number }>;
  /** ADR-0235 — per-site trust-verdict flags + the global kill switch state. */
  getTrustVerdictFlags(): Promise<AdminTrustVerdictFlagsResponse>;
  /** ADR-0235 / ADR-0236 — turn merchant-visible verdicts on / off for one site (ON is gated on readiness; `override` = audited staff override). */
  setTrustVerdictFlag(
    siteId: string,
    live: boolean,
    override?: { readonly confirm: string; readonly reason: string },
  ): Promise<{ ok: true; site_id: string; verdicts_live: boolean; forced?: boolean }>;
  /** ADR-0235 — merchant dispute queue (admin; staff-audited read). */
  getTrustDisputes(status: 'open' | 'resolved' | 'all', offset?: number): Promise<AdminTrustDisputesResponse>;
  /** ADR-0235 — open dispute count for the nav badge. */
  getTrustDisputeCount(): Promise<{ open_count: number }>;
  /** ADR-0235 — resolve a dispute (overturned needs a note). */
  resolveTrustDispute(disputeId: string, outcome: 'upheld' | 'overturned', note: string): Promise<{ ok: true; status: string }>;

  /** ADR-0233 — GET /internal/admin/home-experiment (viewer+). Current
   *  config, funnel roll-up, optional time-series, audit tail. */
  getHomeExperiment(): Promise<AdminHomeExperimentResponse>;
  /** ADR-0233 — PUT /internal/admin/home-experiment (admin only).
   *  Partial body — only the fields present are applied. */
  putHomeExperiment(body: AdminHomeExperimentPutBody): Promise<AdminHomeExperimentResponse>;
  /** ADR-0233 — GET /internal/admin/home-experiment/events (viewer+).
   *  Day-bucketed per-variant counts for the full experiment window. */
  getHomeExperimentEvents(): Promise<AdminHomeExperimentEventsResponse>;
  /** ADR-0234 — agent operator (AI company) enrollment queue. */
  getAgentOperators(status: 'pending' | 'approved' | 'suspended' | 'rejected' | 'draft' | 'all'): Promise<AdminAgentOperatorsResponse>;
  /** ADR-0234 — one operator (staff-audited read). */
  getAgentOperator(operatorId: string): Promise<AdminAgentOperatorDetailResponse>;
  /** ADR-0234 — approve / reject / suspend / reinstate (reject + suspend need a note). */
  reviewAgentOperator(operatorId: string, action: AdminOperatorReviewAction, note: string, confirmRegistryTokens?: boolean): Promise<{ ok: true; status: string; agents_approved: number }>;
  /** ADR-0234 — approve / reject one pending agent identity of an approved operator. */
  reviewAgentOperatorAgent(operatorId: string, agentId: string, action: AdminOperatorAgentReviewAction, confirmRegistryTokens?: boolean): Promise<{ ok: true }>;
  /** ADR-0234 — emergency key revocation by Sill. */
  revokeAgentOperatorKey(operatorId: string, keyId: string, reason: string): Promise<{ ok: true }>;
  /** ADR-0234 — invite an AI company by email (sends a sign-in link).
   *  Idempotent: an existing operator just gets a fresh link. */
  inviteAgentOperator(email: string): Promise<AdminAgentOperatorInviteResponse>;
  /** Bug #16/#17 — read current enrollment mode (open vs invite_only). */
  getAgentOperatorEnrollmentMode(): Promise<{ mode: 'open' | 'invite_only' }>;
  /** Bug #16/#17 — flip the enrollment mode (admin-only). */
  setAgentOperatorEnrollmentMode(mode: 'open' | 'invite_only'): Promise<{ ok: true; mode: 'open' | 'invite_only' }>;

  // --------------------------------------------------------------
  // ADR-0263 S1 — Plans v2 (features, add-ons, per-site plans).
  // All INERT: catalog + staff assignment only; nothing enforces,
  // nothing bills. Writes gated server-side on an admin-equivalent
  // role; reads are support+.
  // --------------------------------------------------------------
  /** PUT /internal/admin/plans/:id/features — REPLACE semantics. */
  putPlanFeatures(
    planId: string,
    body: AdminPlanFeaturesPutBody,
  ): Promise<AdminPlanFeaturesPutResponse>;
  /** GET /internal/admin/addons — list the catalog. */
  getAddons(): Promise<AdminPlanAddonsListResponse>;
  /** GET /internal/admin/addons/:id */
  getAddon(addonId: string): Promise<AdminPlanAddonDetailResponse>;
  /** POST /internal/admin/addons — server mints `addon_id`. */
  createAddon(body: AdminPlanAddonCreateBody): Promise<AdminPlanAddonCreateResponse>;
  /** PATCH /internal/admin/addons/:id — kind + scope NOT patchable. */
  updateAddon(
    addonId: string,
    body: AdminPlanAddonUpdateBody,
  ): Promise<AdminPlanAddonUpdateResponse>;
  /** PUT /internal/admin/addons/:id/prices — REPLACE semantics. */
  putAddonPrices(
    addonId: string,
    body: AdminPlanAddonPricesPutBody,
  ): Promise<AdminPlanAddonPricesPutResponse>;
  /** GET /internal/admin/accounts/:id/site-plans — per-site resolution + default. */
  getAccountSitePlans(accountId: string): Promise<AdminAccountSitePlansResponse>;
  /** ADR-0263 S4 — GET /internal/admin/accounts/:id/features-in-use.
   *  Read-only advisory list of features each site uses outside its
   *  resolved v2 plan. Staff use this before flipping enforcement. */
  getAccountFeaturesInUse(
    accountId: string,
  ): Promise<AdminAccountFeaturesInUseResponse>;
  /** PUT /internal/admin/accounts/:id/sites/:siteId/plan — null clears assignment. */
  putSitePlan(
    accountId: string,
    siteId: string,
    body: AdminSitePlanAssignBody,
  ): Promise<AdminSitePlanAssignResponse>;
  /** GET /internal/admin/accounts/:id/addons */
  getAccountAddons(accountId: string): Promise<AdminAccountAddonsResponse>;
  /** PUT /internal/admin/accounts/:id/addons — single-shot add OR cancel. */
  putAccountAddon(
    accountId: string,
    body: AdminAccountAddonUpdateBody,
  ): Promise<AdminAccountAddonUpdateResponse>;
  /** GET /internal/admin/accounts/:id/site-usage — ADR-0263 S2 meters. */
  getAccountSiteUsage(accountId: string): Promise<AdminAccountSiteUsageResponse>;
}

export function createAdminClient(deps: AdminClientDeps): AdminClient {
  return {
    async requestSignIn(email: string): Promise<void> {
      await deps.http.request<undefined>({
        method: 'POST',
        path: '/internal/admin/auth/request',
        body: { email: email.trim() },
      });
    },

    async consumeToken(token: string): Promise<AdminAuthCallbackResponse> {
      const res = await deps.http.request<AdminAuthCallbackResponse>({
        method: 'POST',
        path: '/internal/admin/auth/callback',
        body: { token },
      });
      if (res === undefined) {
        // 204 on this route would be malformed — the server must echo
        // the StaffPrincipal envelope on success.
        throw new Error('admin_callback_returned_no_body');
      }
      return res;
    },

    async me(opts?: { throwOnUnauthorized?: boolean }): Promise<AdminMeResponse> {
      const res = await deps.http.request<AdminMeResponse>({
        method: 'GET',
        path: '/internal/admin/me',
        ...(opts?.throwOnUnauthorized === true ? { throw_on_unauthorized: true } : {}),
      });
      if (res === undefined) throw new Error('admin_me_returned_no_body');
      return res;
    },

    async logout(): Promise<void> {
      await deps.http.request<undefined>({
        method: 'POST',
        path: '/internal/admin/auth/logout',
      });
    },

    async listAccounts(query: AdminAccountsListQuery): Promise<AdminAccountsListResponse> {
      const params: Record<string, string | number | undefined> = {};
      if (query.search !== undefined && query.search.length > 0) {
        params['search'] = query.search;
      }
      if (query.limit !== undefined) params['limit'] = query.limit;
      if (query.offset !== undefined) params['offset'] = query.offset;
      if (query.sort !== undefined) params['sort'] = query.sort;
      if (query.include_deleted === true) params['include_deleted'] = 1;
      if (query.dir !== undefined) params['dir'] = query.dir;
      const res = await deps.http.request<AdminAccountsListResponse>({
        method: 'GET',
        path: '/internal/admin/accounts',
        query: params,
      });
      if (res === undefined) throw new Error('admin_list_accounts_returned_no_body');
      return res;
    },

    async getAccount(accountId: string): Promise<AdminAccountDetailResponse> {
      const res = await deps.http.request<AdminAccountDetailResponse>({
        method: 'GET',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}`,
      });
      if (res === undefined) throw new Error('admin_get_account_returned_no_body');
      return res;
    },

    async updateAccount(
      accountId: string,
      body: AdminAccountUpdateBody,
    ): Promise<AdminAccountUpdateResponse> {
      const res = await deps.http.request<AdminAccountUpdateResponse>({
        method: 'PATCH',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}`,
        body,
      });
      if (res === undefined) throw new Error('admin_update_account_returned_no_body');
      return res;
    },

    async updateBilling(
      accountId: string,
      body: AdminBillingUpdateBody,
    ): Promise<AdminBillingUpdateResponse> {
      const res = await deps.http.request<AdminBillingUpdateResponse>({
        method: 'PUT',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/billing`,
        body,
      });
      if (res === undefined) throw new Error('admin_update_billing_returned_no_body');
      return res;
    },

    async deleteAccount(accountId: string, confirm: string): Promise<void> {
      await deps.http.request<undefined>({
        method: 'DELETE',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}`,
        body: { confirm },
      });
    },

    async deleteSite(accountId: string, siteId: string): Promise<void> {
      await deps.http.request<undefined>({
        method: 'DELETE',
        path:
          `/internal/admin/accounts/${encodeURIComponent(accountId)}` +
          `/sites/${encodeURIComponent(siteId)}`,
      });
    },

    async createSupportLogin(
      accountId: string,
      allowChanges?: boolean,
    ): Promise<AdminSupportLoginCreateResponse> {
      const res = await deps.http.request<AdminSupportLoginCreateResponse>({
        method: 'POST',
        path:
          `/internal/admin/accounts/${encodeURIComponent(accountId)}` +
          `/support-login`,
        // Only the mode is client-chosen; everything else comes from the
        // staff session + the path-param account_id.
        body: { allow_changes: allowChanges === true },
      });
      if (res === undefined) {
        throw new Error('admin_create_support_login_returned_no_body');
      }
      return res;
    },

    // ADR-0237 — feature toggles (agency, white_label).
    async getAccountFeatures(accountId: string): Promise<AdminAccountFeatures> {
      const res = await deps.http.request<AdminAccountFeatures>({
        method: 'GET',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/features`,
      });
      if (res === undefined) throw new Error('admin_get_features_returned_no_body');
      return res;
    },

    async setAccountAgencyFeature(
      accountId: string,
      enabled: boolean,
    ): Promise<AdminAccountFeatures> {
      // Server echoes `{ ok, feature_key, enabled }`; we fetch the
      // full features shape right after so the caller gets one
      // authoritative snapshot. The POST + GET pair is intentional —
      // the server's write response is not the aggregate shape.
      await deps.http.request<{ ok: true; feature_key: 'agency'; enabled: boolean }>({
        method: 'POST',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/features/agency`,
        body: { enabled },
      });
      const after = await deps.http.request<AdminAccountFeatures>({
        method: 'GET',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/features`,
      });
      if (after === undefined) throw new Error('admin_get_features_returned_no_body');
      return after;
    },

    async setAccountWhiteLabelFeature(
      accountId: string,
      enabled: boolean,
    ): Promise<AdminAccountFeatures> {
      await deps.http.request<{ ok: true; feature_key: 'white_label'; enabled: boolean }>({
        method: 'POST',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/features/white-label`,
        body: { enabled },
      });
      const after = await deps.http.request<AdminAccountFeatures>({
        method: 'GET',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/features`,
      });
      if (after === undefined) throw new Error('admin_get_features_returned_no_body');
      return after;
    },

    // ─── ops2026 Agencies panel (founder request 2026-10-29) ─────
    async listAgencies(query: { readonly q?: string }): Promise<AdminAgenciesListResponse> {
      const res = await deps.http.request<AdminAgenciesListResponse>({
        method: 'GET',
        path: '/internal/admin/agencies',
        query: query.q !== undefined && query.q.length > 0 ? { q: query.q } : {},
      });
      if (res === undefined) throw new Error('admin_list_agencies_returned_no_body');
      return res;
    },

    async getAgencyDetail(agencyAccountId: string): Promise<AdminAgencyDetailResponse> {
      const res = await deps.http.request<AdminAgencyDetailResponse>({
        method: 'GET',
        path: `/internal/admin/agencies/${encodeURIComponent(agencyAccountId)}`,
      });
      if (res === undefined) throw new Error('admin_agency_detail_returned_no_body');
      return res;
    },

    async addAgencyClient(
      agencyAccountId: string,
      body: AdminAgencyAddClientBody,
    ): Promise<AdminAgencyAddClientResponse> {
      const res = await deps.http.request<AdminAgencyAddClientResponse>({
        method: 'POST',
        path: `/internal/admin/agencies/${encodeURIComponent(agencyAccountId)}/clients`,
        body,
      });
      if (res === undefined) throw new Error('admin_add_agency_client_returned_no_body');
      return res;
    },

    async removeAgencyClient(
      agencyAccountId: string,
      linkId: string,
      body: AdminAgencyRemoveClientBody,
    ): Promise<AdminAgencyRemoveClientResponse> {
      const res = await deps.http.request<AdminAgencyRemoveClientResponse>({
        method: 'DELETE',
        path: `/internal/admin/agencies/${encodeURIComponent(agencyAccountId)}/clients/${encodeURIComponent(linkId)}`,
        body,
      });
      if (res === undefined) throw new Error('admin_remove_agency_client_returned_no_body');
      return res;
    },

    async moveAgencyClient(
      agencyAccountId: string,
      linkId: string,
      body: AdminAgencyMoveClientBody,
    ): Promise<AdminAgencyMoveClientResponse> {
      const res = await deps.http.request<AdminAgencyMoveClientResponse>({
        method: 'POST',
        path: `/internal/admin/agencies/${encodeURIComponent(agencyAccountId)}/clients/${encodeURIComponent(linkId)}/move`,
        body,
      });
      if (res === undefined) throw new Error('admin_move_agency_client_returned_no_body');
      return res;
    },

    async setAgencyOfRecord(
      accountId: string,
      body: AdminAgencyOfRecordBody,
    ): Promise<AdminAgencyOfRecordResponse> {
      const res = await deps.http.request<AdminAgencyOfRecordResponse>({
        method: 'PUT',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/agency-of-record`,
        body,
      });
      if (res === undefined) throw new Error('admin_set_agency_of_record_returned_no_body');
      return res;
    },

    async setAgencyPaysForSites(
      agencyAccountId: string,
      linkId: string,
      body: AdminAgencyPaysForSitesBody,
    ): Promise<AdminAgencyPaysForSitesResponse> {
      const res = await deps.http.request<AdminAgencyPaysForSitesResponse>({
        method: 'PUT',
        path: `/internal/admin/agencies/${encodeURIComponent(agencyAccountId)}/clients/${encodeURIComponent(linkId)}/agency-pays`,
        body,
      });
      if (res === undefined) throw new Error('admin_set_agency_pays_returned_no_body');
      return res;
    },

    // ─── ADR-0263 §8 — self-serve agency-plan requests ────────────
    async listAgencyRequests(): Promise<AdminAgencyRequestsListResponse> {
      const res = await deps.http.request<AdminAgencyRequestsListResponse>({
        method: 'GET',
        path: '/internal/admin/agency-requests',
      });
      if (res === undefined) throw new Error('admin_list_agency_requests_returned_no_body');
      return res;
    },

    async dismissAgencyRequest(
      accountId: string,
    ): Promise<AdminAgencyRequestDismissResponse> {
      const res = await deps.http.request<AdminAgencyRequestDismissResponse>({
        method: 'POST',
        path: `/internal/admin/agency-requests/${encodeURIComponent(accountId)}/dismiss`,
        body: {},
      });
      if (res === undefined) throw new Error('admin_dismiss_agency_request_returned_no_body');
      return res;
    },

    async getMetricsOverview(range: ReportingRange): Promise<AdminMetricsOverview> {
      const res = await deps.http.request<AdminMetricsOverview>({
        method: 'GET',
        path: '/internal/admin/metrics/overview',
        query: { range },
      });
      if (res === undefined) throw new Error('admin_metrics_overview_returned_no_body');
      return res;
    },

    async getMetricsAssistants(range: ReportingRange): Promise<AdminMetricsAssistants> {
      const res = await deps.http.request<AdminMetricsAssistants>({
        method: 'GET',
        path: '/internal/admin/metrics/assistants',
        query: { range },
      });
      if (res === undefined) throw new Error('admin_metrics_assistants_returned_no_body');
      return res;
    },

    async getChurnRisk(): Promise<AdminChurnRiskResponse> {
      const res = await deps.http.request<AdminChurnRiskResponse>({
        method: 'GET',
        path: '/internal/admin/metrics/churn-risk',
      });
      if (res === undefined) throw new Error('admin_churn_risk_returned_no_body');
      return res;
    },

    async getHealthChecks(): Promise<AdminHealthReport> {
      const res = await deps.http.request<AdminHealthReport>({
        method: 'GET',
        path: '/internal/admin/health/checks',
      });
      if (res === undefined) throw new Error('admin_health_checks_returned_no_body');
      return res;
    },

    async getInstallFleetPlan(): Promise<AdminInstallFleetPlan> {
      const res = await deps.http.request<AdminInstallFleetPlan>({
        method: 'GET',
        path: '/internal/admin/install/fleet-plan',
      });
      if (res === undefined) throw new Error('admin_install_fleet_plan_returned_no_body');
      return res;
    },

    async runInstallFleetRedeploy(body: AdminInstallFleetRunBody): Promise<AdminInstallFleetRunResult> {
      const res = await deps.http.request<AdminInstallFleetRunResult>({
        method: 'POST',
        path: '/internal/admin/install/fleet-redeploy',
        body,
      });
      if (res === undefined) throw new Error('admin_install_fleet_redeploy_returned_no_body');
      return res;
    },

    async getInstallFleetRuns(): Promise<AdminInstallFleetRunsResponse> {
      const res = await deps.http.request<AdminInstallFleetRunsResponse>({
        method: 'GET',
        path: '/internal/admin/install/fleet-runs',
      });
      if (res === undefined) throw new Error('admin_install_fleet_runs_returned_no_body');
      return res;
    },

    async getInstallFleetRun(run_id: string): Promise<AdminInstallFleetRunDetail> {
      const res = await deps.http.request<AdminInstallFleetRunDetail>({
        method: 'GET',
        path: `/internal/admin/install/fleet-runs/${encodeURIComponent(run_id)}`,
      });
      if (res === undefined) throw new Error('admin_install_fleet_run_returned_no_body');
      return res;
    },

    async getVerifiedCrawlerReview(): Promise<AdminVerifiedCrawlerReviewResponse> {
      const res = await deps.http.request<AdminVerifiedCrawlerReviewResponse>({
        method: 'GET',
        path: '/internal/admin/verified-crawlers',
      });
      if (res === undefined) throw new Error('admin_verified_crawlers_returned_no_body');
      return res;
    },

    async fetchVerifiedCrawlers(): Promise<AdminVerifiedCrawlerFetchResponse> {
      const res = await deps.http.request<AdminVerifiedCrawlerFetchResponse>({
        method: 'POST',
        path: '/internal/admin/verified-crawlers/fetch',
        body: {},
      });
      if (res === undefined) throw new Error('admin_verified_crawlers_fetch_returned_no_body');
      return res;
    },

    async approveVerifiedCrawlerSnapshot(
      snapshotId: string,
      body: AdminVerifiedCrawlerApproveRequest,
    ): Promise<AdminVerifiedCrawlerApproveResponse> {
      const res = await deps.http.request<AdminVerifiedCrawlerApproveResponse>({
        method: 'POST',
        path: `/internal/admin/verified-crawlers/snapshots/${encodeURIComponent(snapshotId)}/approve`,
        body,
      });
      if (res === undefined) throw new Error('admin_verified_crawlers_approve_returned_no_body');
      return res;
    },

    async rejectVerifiedCrawlerSnapshot(
      snapshotId: string,
      body: AdminVerifiedCrawlerRejectRequest,
    ): Promise<AdminVerifiedCrawlerRejectResponse> {
      const res = await deps.http.request<AdminVerifiedCrawlerRejectResponse>({
        method: 'POST',
        path: `/internal/admin/verified-crawlers/snapshots/${encodeURIComponent(snapshotId)}/reject`,
        body,
      });
      if (res === undefined) throw new Error('admin_verified_crawlers_reject_returned_no_body');
      return res;
    },

    async getAttackPathDecisions(status?: string): Promise<AdminAttackPathDecisionsResponse> {
      const q = status !== undefined && status.length > 0
        ? `?status=${encodeURIComponent(status)}` : '';
      const res = await deps.http.request<AdminAttackPathDecisionsResponse>({
        method: 'GET',
        path: `/internal/admin/attack-path-decisions${q}`,
      });
      if (res === undefined) throw new Error('admin_attack_path_decisions_returned_no_body');
      return res;
    },

    async approveAttackPathDecision(additionId: string): Promise<AdminAttackPathDecisionOverrideResponse> {
      const res = await deps.http.request<AdminAttackPathDecisionOverrideResponse>({
        method: 'POST',
        path: `/internal/admin/attack-path-decisions/${encodeURIComponent(additionId)}/approve`,
        body: {},
      });
      if (res === undefined) throw new Error('admin_attack_path_decisions_approve_returned_no_body');
      return res;
    },

    async removeAttackPathDecision(additionId: string): Promise<AdminAttackPathDecisionOverrideResponse> {
      const res = await deps.http.request<AdminAttackPathDecisionOverrideResponse>({
        method: 'POST',
        path: `/internal/admin/attack-path-decisions/${encodeURIComponent(additionId)}/remove`,
        body: {},
      });
      if (res === undefined) throw new Error('admin_attack_path_decisions_remove_returned_no_body');
      return res;
    },

    async getCiRuns(): Promise<AdminCiRunsResponse> {
      const res = await deps.http.request<AdminCiRunsResponse>({
        method: 'GET',
        path: '/internal/admin/qa/ci-runs',
      });
      if (res === undefined) throw new Error('admin_ci_runs_returned_no_body');
      return res;
    },

    async startSmokeRun(accountId: string, siteId?: string): Promise<AdminSmokeRunStartResponse> {
      const res = await deps.http.request<AdminSmokeRunStartResponse>({
        method: 'POST',
        path: '/internal/admin/smoke-runs',
        body: siteId !== undefined ? { account_id: accountId, site_id: siteId } : { account_id: accountId },
      });
      if (res === undefined) throw new Error('admin_smoke_start_returned_no_body');
      return res;
    },

    async advanceSmokeRun(smokeRunId: string): Promise<AdminSmokeRunDetail> {
      const res = await deps.http.request<AdminSmokeRunDetail>({
        method: 'POST',
        path: `/internal/admin/smoke-runs/${encodeURIComponent(smokeRunId)}/advance`,
        // The advance body is DELIBERATELY empty — the server refuses
        // any client-supplied field so the sequence stays server-only
        // (ADR-0176 §3 state machine + Required test 3).
        body: {},
      });
      if (res === undefined) throw new Error('admin_smoke_advance_returned_no_body');
      return res;
    },

    async getSmokeRun(smokeRunId: string): Promise<AdminSmokeRunDetail> {
      const res = await deps.http.request<AdminSmokeRunDetail>({
        method: 'GET',
        path: `/internal/admin/smoke-runs/${encodeURIComponent(smokeRunId)}`,
      });
      if (res === undefined) throw new Error('admin_smoke_get_returned_no_body');
      return res;
    },

    async listSmokeRuns(query?: {
      account_id?: string;
      status?: string;
      limit?: number;
    }): Promise<AdminSmokeRunsListResponse> {
      const q: Record<string, string> = {};
      if (query?.account_id !== undefined) q['account_id'] = query.account_id;
      if (query?.status !== undefined) q['status'] = query.status;
      if (query?.limit !== undefined) q['limit'] = String(query.limit);
      const search = new URLSearchParams(q).toString();
      const suffix = search.length > 0 ? `?${search}` : '';
      const res = await deps.http.request<AdminSmokeRunsListResponse>({
        method: 'GET',
        path: `/internal/admin/smoke-runs${suffix}`,
      });
      if (res === undefined) throw new Error('admin_smoke_list_returned_no_body');
      return res;
    },

    async cancelSmokeRun(smokeRunId: string): Promise<AdminSmokeRunDetail> {
      const res = await deps.http.request<AdminSmokeRunDetail>({
        method: 'POST',
        path: `/internal/admin/smoke-runs/${encodeURIComponent(smokeRunId)}/cancel`,
      });
      if (res === undefined) throw new Error('admin_smoke_cancel_returned_no_body');
      return res;
    },

    async startAdHocSmokeRun(
      accountId: string,
      siteId: string,
      skill: AdminAdHocSmokeSkill,
    ): Promise<AdminSmokeRunAdHocResponse> {
      // The `skill` param is compile-time restricted to the three
      // non-transactional values via the `AdminAdHocSmokeSkill`
      // union; the server enforces the same allowlist and would
      // reject anything else with 400 `invalid_ad_hoc_skill`. Body
      // is snake_case per CLAUDE.md §3 / spec §5.
      const res = await deps.http.request<AdminSmokeRunAdHocResponse>({
        method: 'POST',
        path: '/internal/admin/smoke-runs/ad-hoc',
        body: {
          account_id: accountId,
          site_id: siteId,
          skill,
        },
      });
      if (res === undefined) throw new Error('admin_smoke_ad_hoc_returned_no_body');
      return res;
    },

    async startProtocolTest(args: {
      site_id?: string;
      domain?: string;
      protocol: AdminProtocolTestProtocol;
      cart_item_id?: string;
    }): Promise<AdminProtocolTestRunDetail> {
      const res = await deps.http.request<AdminProtocolTestRunDetail>({
        method: 'POST',
        path: '/internal/admin/protocol-tests',
        body: args,
      });
      if (res === undefined) throw new Error('admin_protocol_test_start_returned_no_body');
      return res;
    },

    async getProtocolTest(runId: string): Promise<AdminProtocolTestRunDetail> {
      const res = await deps.http.request<AdminProtocolTestRunDetail>({
        method: 'GET',
        path: `/internal/admin/protocol-tests/${encodeURIComponent(runId)}`,
      });
      if (res === undefined) throw new Error('admin_protocol_test_get_returned_no_body');
      return res;
    },

    async payHeldOrder(runId: string): Promise<AdminProtocolTestRunDetail> {
      const res = await deps.http.request<AdminProtocolTestRunDetail>({
        method: 'POST',
        path: `/internal/admin/protocol-tests/${encodeURIComponent(runId)}/pay-held-order`,
      });
      if (res === undefined) throw new Error('admin_protocol_test_pay_held_order_returned_no_body');
      return res;
    },

    async listProtocolTests(query?: {
      account_id?: string;
      site_id?: string;
      protocol?: AdminProtocolTestProtocol;
      limit?: number;
    }): Promise<AdminProtocolTestRunsListResponse> {
      const q: Record<string, string> = {};
      if (query?.account_id !== undefined) q['account_id'] = query.account_id;
      if (query?.site_id !== undefined) q['site_id'] = query.site_id;
      if (query?.protocol !== undefined) q['protocol'] = query.protocol;
      if (query?.limit !== undefined) q['limit'] = String(query.limit);
      const search = new URLSearchParams(q).toString();
      const suffix = search.length > 0 ? `?${search}` : '';
      const res = await deps.http.request<AdminProtocolTestRunsListResponse>({
        method: 'GET',
        path: `/internal/admin/protocol-tests${suffix}`,
      });
      if (res === undefined) throw new Error('admin_protocol_test_list_returned_no_body');
      return res;
    },

    async escalateIssue(body: AdminEscalateBody): Promise<AdminEscalateResponse> {
      // The route surfaces 409 (not-configured) and 429 (rate-limited)
      // distinctly — the http-client maps both to ApiError (kind
      // 'conflict' / 'rate_limited'), and the caller branches on
      // `err.kind` to render the right affordance. We do NOT swallow
      // them here so the caller can choose the wording.
      const res = await deps.http.request<AdminEscalateResponse>({
        method: 'POST',
        path: '/internal/admin/qa/escalate',
        body,
      });
      if (res === undefined) throw new Error('admin_escalate_returned_no_body');
      return res;
    },

    async getPlans(): Promise<AdminPlansListResponse> {
      const res = await deps.http.request<AdminPlansListResponse>({
        method: 'GET',
        path: '/internal/admin/plans',
      });
      if (res === undefined) throw new Error('admin_list_plans_returned_no_body');
      return res;
    },

    async getPlan(planId: string): Promise<AdminPlanDetailResponse> {
      const res = await deps.http.request<AdminPlanDetailResponse>({
        method: 'GET',
        path: `/internal/admin/plans/${encodeURIComponent(planId)}`,
      });
      if (res === undefined) throw new Error('admin_get_plan_returned_no_body');
      return res;
    },

    async createPlan(body: AdminPlanCreateBody): Promise<AdminPlanCreateResponse> {
      const res = await deps.http.request<AdminPlanCreateResponse>({
        method: 'POST',
        path: '/internal/admin/plans',
        body,
      });
      if (res === undefined) throw new Error('admin_create_plan_returned_no_body');
      return res;
    },

    async syncBillingProviders(): Promise<AdminBillingSyncResponse> {
      const res = await deps.http.request<AdminBillingSyncResponse>({
        method: 'POST',
        path: '/internal/admin/billing/provider-sync',
        body: {},
      });
      if (res === undefined) throw new Error('admin_billing_sync_returned_no_body');
      return res;
    },

    async syncBillingProvidersV2(): Promise<AdminBillingSyncV2Response> {
      const res = await deps.http.request<AdminBillingSyncV2Response>({
        method: 'POST',
        path: '/internal/admin/billing/provider-sync-v2',
        body: {},
      });
      if (res === undefined) throw new Error('admin_billing_sync_v2_returned_no_body');
      return res;
    },

    async getPricingPreview(): Promise<AdminPricingPreviewResponse> {
      const res = await deps.http.request<AdminPricingPreviewResponse>({
        method: 'GET',
        path: '/internal/admin/pricing-preview',
      });
      if (res === undefined) throw new Error('admin_pricing_preview_returned_no_body');
      return res;
    },

    async updatePlan(
      planId: string,
      body: AdminPlanUpdateBody,
    ): Promise<AdminPlanUpdateResponse> {
      const res = await deps.http.request<AdminPlanUpdateResponse>({
        method: 'PATCH',
        path: `/internal/admin/plans/${encodeURIComponent(planId)}`,
        body,
      });
      if (res === undefined) throw new Error('admin_update_plan_returned_no_body');
      return res;
    },

    async putPlanPrices(
      planId: string,
      body: AdminPlanPricesPutBody,
    ): Promise<AdminPlanPricesPutResponse> {
      const res = await deps.http.request<AdminPlanPricesPutResponse>({
        method: 'PUT',
        path: `/internal/admin/plans/${encodeURIComponent(planId)}/prices`,
        body,
      });
      if (res === undefined) throw new Error('admin_put_plan_prices_returned_no_body');
      return res;
    },

    async putPlanEntitlements(
      planId: string,
      body: AdminPlanEntitlementsPutBody,
    ): Promise<AdminPlanEntitlementsPutResponse> {
      const res = await deps.http.request<AdminPlanEntitlementsPutResponse>({
        method: 'PUT',
        path: `/internal/admin/plans/${encodeURIComponent(planId)}/entitlements`,
        body,
      });
      if (res === undefined) {
        throw new Error('admin_put_plan_entitlements_returned_no_body');
      }
      return res;
    },

    async assignSubscription(
      accountId: string,
      body: AdminSubscriptionAssignBody,
    ): Promise<AdminSubscriptionAssignResponse> {
      // The route returns HTTP 409 when the chosen plan is archived;
      // the http-client maps that to ApiError(kind='conflict') and the
      // calling view branches on `err.kind === 'conflict'` to render
      // the "pick an active plan" affordance, NOT a generic error toast.
      const res = await deps.http.request<AdminSubscriptionAssignResponse>({
        method: 'PUT',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/subscription`,
        body,
      });
      if (res === undefined) {
        throw new Error('admin_assign_subscription_returned_no_body');
      }
      return res;
    },

    async putEntitlementOverrides(
      accountId: string,
      body: AdminEntitlementOverridesPutBody,
    ): Promise<AdminEntitlementOverridesPutResponse> {
      const res = await deps.http.request<AdminEntitlementOverridesPutResponse>({
        method: 'PUT',
        path:
          `/internal/admin/accounts/${encodeURIComponent(accountId)}` +
          `/entitlement-overrides`,
        body,
      });
      if (res === undefined) {
        throw new Error('admin_put_entitlement_overrides_returned_no_body');
      }
      return res;
    },

    async setEnforcement(
      accountId: string,
      enabled: boolean,
    ): Promise<AdminEnforcementToggleResponse> {
      const res = await deps.http.request<AdminEnforcementToggleResponse>({
        method: 'PUT',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/enforcement`,
        body: { enabled },
      });
      if (res === undefined) {
        throw new Error('admin_set_enforcement_returned_no_body');
      }
      return res;
    },

    async getAccountUsage(accountId: string): Promise<AdminAccountUsageResponse> {
      const res = await deps.http.request<AdminAccountUsageResponse>({
        method: 'GET',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/usage`,
      });
      if (res === undefined) throw new Error('admin_get_account_usage_returned_no_body');
      return res;
    },

    async listSettings(): Promise<AdminSettingsResponse> {
      const res = await deps.http.request<AdminSettingsResponse>({
        method: 'GET',
        path: '/internal/admin/settings',
      });
      if (res === undefined) throw new Error('admin_settings_list_returned_no_body');
      return res;
    },

    async getSettingsHistory(params: {
      limit: number;
      offset: number;
      sort: 'at' | 'key' | 'staff';
      dir: 'asc' | 'desc';
    }): Promise<AdminSettingHistoryResponse> {
      const qs = new URLSearchParams({
        limit: String(params.limit),
        offset: String(params.offset),
        sort: params.sort,
        dir: params.dir,
      });
      const res = await deps.http.request<AdminSettingHistoryResponse>({
        method: 'GET',
        path: `/internal/admin/settings/history?${qs.toString()}`,
      });
      if (res === undefined) throw new Error('admin_settings_history_returned_no_body');
      return res;
    },

    async setSetting(key: string, value: boolean | number): Promise<AdminSettingsResponse> {
      const res = await deps.http.request<AdminSettingsResponse>({
        method: 'PUT',
        path: `/internal/admin/settings/${encodeURIComponent(key)}`,
        body: { value },
      });
      if (res === undefined) throw new Error('admin_settings_put_returned_no_body');
      return res;
    },

    async runAcpProbe(body?: AdminAcpProbeRequest): Promise<AdminAcpProbeResult> {
      const res = await deps.http.request<AdminAcpProbeResult>({
        method: 'POST',
        path: '/internal/admin/acp-connect-probe',
        body: body ?? {},
      });
      if (res === undefined) throw new Error('admin_acp_probe_returned_no_body');
      return res;
    },

    async runUcpCheckoutTest(
      accountId: string,
      siteId: string,
      body?: AdminUcpCheckoutTestRequest,
    ): Promise<AdminUcpCheckoutTestResponse> {
      // The route path carries account + site; no body. Server
      // generates a per-run bearer + keypair in memory, exercises the
      // merchant's UCP relay in-process, and returns the per-step
      // outcomes synchronously. Server-side 6/hour/staff rate limit
      // 429s; the caller renders a generic retry banner on 429.
      const res = await deps.http.request<AdminUcpCheckoutTestResponse>({
        method: 'POST',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/sites/${encodeURIComponent(siteId)}/ucp-checkout-test`,
        // ADR-0254 — optional discount code; omitted entirely when unset.
        ...(body !== undefined ? { body } : {}),
      });
      if (res === undefined) throw new Error('admin_ucp_checkout_test_returned_no_body');
      return res;
    },

    async finishUcpCheckoutTest(
      accountId: string,
      siteId: string,
      body: AdminUcpCheckoutTestFinishRequest,
    ): Promise<AdminUcpCheckoutTestResponse> {
      const res = await deps.http.request<AdminUcpCheckoutTestResponse>({
        method: 'POST',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/sites/${encodeURIComponent(siteId)}/ucp-checkout-test/finish`,
        body,
      });
      if (res === undefined) throw new Error('admin_ucp_checkout_test_finish_returned_no_body');
      return res;
    },

    async listDeletionRuns(): Promise<AdminDeletionRunListResponse> {
      const res = await deps.http.request<AdminDeletionRunListResponse>({
        method: 'GET', path: '/internal/admin/deletion-runs',
      });
      if (res === undefined) throw new Error('admin_deletion_runs_list_returned_no_body');
      return res;
    },

    async approveDeletionRun(id: string): Promise<AdminDeletionRunResponse> {
      const res = await deps.http.request<AdminDeletionRunResponse>({
        method: 'POST',
        path: `/internal/admin/deletion-runs/${encodeURIComponent(id)}/approve`,
      });
      if (res === undefined) throw new Error('admin_deletion_run_approve_returned_no_body');
      return res;
    },

    async rejectDeletionRun(id: string, reason: string): Promise<AdminDeletionRunResponse> {
      const res = await deps.http.request<AdminDeletionRunResponse>({
        method: 'POST',
        path: `/internal/admin/deletion-runs/${encodeURIComponent(id)}/reject`,
        body: { reason },
      });
      if (res === undefined) throw new Error('admin_deletion_run_reject_returned_no_body');
      return res;
    },

    async listLeads(q): Promise<AdminLeadsResponse> {
      const params = new URLSearchParams();
      if (q.status !== undefined) params.set('status', q.status);
      if (q.q !== undefined && q.q.length > 0) params.set('q', q.q);
      if (q.limit !== undefined) params.set('limit', String(q.limit));
      if (q.offset !== undefined) params.set('offset', String(q.offset));
      if (q.kind !== undefined) params.set('kind', q.kind);
      const qs = params.toString();
      const res = await deps.http.request<AdminLeadsResponse>({
        method: 'GET',
        path: `/internal/admin/leads${qs.length > 0 ? `?${qs}` : ''}`,
      });
      if (res === undefined) throw new Error('admin_leads_list_returned_no_body');
      return res;
    },

    async listStaff() {
      const res = await deps.http.request<AdminStaffListResponse>({ method: 'GET', path: '/internal/admin/staff' });
      if (res === undefined) throw new Error('admin_staff_list_returned_no_body');
      return res;
    },

    async requestStaff(body) {
      const res = await deps.http.request<{ staff: AdminStaffMember | null }>({
        method: 'POST', path: '/internal/admin/staff', body,
      });
      if (res === undefined) throw new Error('admin_staff_request_returned_no_body');
      return res;
    },

    async staffStepUp(body) {
      const res = await deps.http.request<{ sent_to: string; expires_at: string }>({
        method: 'POST', path: '/internal/admin/staff/step-up', body,
      });
      if (res === undefined) throw new Error('admin_staff_step_up_returned_no_body');
      return res;
    },

    async staffAction(staffId, action, body) {
      const res = await deps.http.request<{ staff: AdminStaffMember | null }>({
        method: 'POST',
        path: `/internal/admin/staff/${encodeURIComponent(staffId)}/${action}`,
        body,
      });
      if (res === undefined) throw new Error('admin_staff_action_returned_no_body');
      return res;
    },

    async listStaffLog(q) {
      const params = new URLSearchParams();
      if (q.action !== undefined && q.action.length > 0) params.set('action', q.action);
      if (q.q !== undefined && q.q.length > 0) params.set('q', q.q);
      if (q.limit !== undefined) params.set('limit', String(q.limit));
      if (q.offset !== undefined) params.set('offset', String(q.offset));
      const qs = params.toString();
      const res = await deps.http.request<AdminStaffLogResponse>({
        method: 'GET',
        path: `/internal/admin/staff-log${qs.length > 0 ? `?${qs}` : ''}`,
      });
      if (res === undefined) throw new Error('admin_staff_log_returned_no_body');
      return res;
    },

    async sendLeadReport(body) {
      const res = await deps.http.request<AdminSendReportResult>({
        method: 'POST',
        path: '/internal/admin/leads/send-report',
        body,
      });
      if (res === undefined) throw new Error('admin_leads_send_report_returned_no_body');
      return res;
    },

    async createReportLinks(body) {
      const res = await deps.http.request<AdminReportLinksResponse>({
        method: 'POST',
        path: '/internal/admin/leads/report-links',
        body,
      });
      if (res === undefined) throw new Error('admin_leads_report_links_returned_no_body');
      return res;
    },

    async deleteLeads(body) {
      const res = await deps.http.request<{ lead_rows_deleted: number; scan_rows_deleted: number }>({
        method: 'POST',
        path: '/internal/admin/leads/delete',
        body,
      });
      if (res === undefined) throw new Error('admin_leads_delete_returned_no_body');
      return res;
    },

    async listJobs(): Promise<AdminJobsResponse> {
      const res = await deps.http.request<AdminJobsResponse>({
        method: 'GET',
        path: '/internal/admin/jobs',
      });
      if (res === undefined) throw new Error('admin_jobs_list_returned_no_body');
      return res;
    },

    async listPurgeRuns(): Promise<AdminPurgeListResponse> {
      const res = await deps.http.request<AdminPurgeListResponse>({
        method: 'GET',
        path: '/internal/admin/purge-runs',
      });
      if (res === undefined) throw new Error('admin_purge_list_returned_no_body');
      return res;
    },

    async approvePurgeRun(purgeRunId: string): Promise<AdminPurgeApproveResponse> {
      const res = await deps.http.request<AdminPurgeApproveResponse>({
        method: 'POST',
        path: `/internal/admin/purge-runs/${encodeURIComponent(purgeRunId)}/approve`,
      });
      if (res === undefined) throw new Error('admin_purge_approve_returned_no_body');
      return res;
    },

    async rejectPurgeRun(
      purgeRunId: string,
      reason: string,
    ): Promise<AdminPurgeRejectResponse> {
      const res = await deps.http.request<AdminPurgeRejectResponse>({
        method: 'POST',
        path: `/internal/admin/purge-runs/${encodeURIComponent(purgeRunId)}/reject`,
        body: { reason },
      });
      if (res === undefined) throw new Error('admin_purge_reject_returned_no_body');
      return res;
    },

    async getTrustVerdictFlags(): Promise<AdminTrustVerdictFlagsResponse> {
      const res = await deps.http.request<AdminTrustVerdictFlagsResponse>({ method: 'GET', path: '/internal/admin/trust-verdict-flags' });
      if (res === undefined) throw new Error('admin_trust_flags_returned_no_body');
      return res;
    },

    /**
     * ADR-0236 — turning a site ON is refused (409 `not_ready`, body has
     * `missing`) unless it meets the readiness targets; `override` is the
     * typed-confirmation staff override (audited).
     */
    async setTrustVerdictFlag(
      siteId: string,
      live: boolean,
      override?: { readonly confirm: string; readonly reason: string },
    ): Promise<{ ok: true; site_id: string; verdicts_live: boolean; forced?: boolean }> {
      const body: SetTrustVerdictFlagRequest = override === undefined
        ? { live }
        : { live, force: true, confirm: override.confirm, reason: override.reason };
      const res = await deps.http.request<{ ok: true; site_id: string; verdicts_live: boolean; forced?: boolean }>({
        method: 'PUT',
        path: `/internal/admin/trust-verdict-flags/${encodeURIComponent(siteId)}`,
        body,
      });
      if (res === undefined) throw new Error('admin_trust_flag_put_returned_no_body');
      return res;
    },

    async getTrustDisputes(status: 'open' | 'resolved' | 'all', offset = 0): Promise<AdminTrustDisputesResponse> {
      const qs = new URLSearchParams({ status, offset: String(offset), limit: '50' });
      const res = await deps.http.request<AdminTrustDisputesResponse>({ method: 'GET', path: `/internal/admin/trust-disputes?${qs.toString()}` });
      if (res === undefined) throw new Error('admin_trust_disputes_returned_no_body');
      return res;
    },

    async getTrustDisputeCount(): Promise<{ open_count: number }> {
      const res = await deps.http.request<{ open_count: number }>({ method: 'GET', path: '/internal/admin/trust-disputes/count' });
      if (res === undefined) throw new Error('admin_trust_dispute_count_returned_no_body');
      return res;
    },

    async getAgentOperators(status): Promise<AdminAgentOperatorsResponse> {
      const res = await deps.http.request<AdminAgentOperatorsResponse>({ method: 'GET', path: `/internal/admin/agent-operators?status=${encodeURIComponent(status)}` });
      if (res === undefined) throw new Error('admin_agent_operators_returned_no_body');
      return res;
    },

    async getAgentOperator(operatorId: string): Promise<AdminAgentOperatorDetailResponse> {
      const res = await deps.http.request<AdminAgentOperatorDetailResponse>({ method: 'GET', path: `/internal/admin/agent-operators/${encodeURIComponent(operatorId)}` });
      if (res === undefined) throw new Error('admin_agent_operator_returned_no_body');
      return res;
    },

    async reviewAgentOperator(operatorId: string, action: AdminOperatorReviewAction, note: string, confirmRegistryTokens = false): Promise<{ ok: true; status: string; agents_approved: number }> {
      const res = await deps.http.request<{ ok: true; status: string; agents_approved: number }>({
        method: 'POST', path: `/internal/admin/agent-operators/${encodeURIComponent(operatorId)}/review`, body: { action, note, confirm_registry_tokens: confirmRegistryTokens },
      });
      if (res === undefined) throw new Error('admin_agent_operator_review_returned_no_body');
      return res;
    },

    async reviewAgentOperatorAgent(operatorId: string, agentId: string, action: AdminOperatorAgentReviewAction, confirmRegistryTokens = false): Promise<{ ok: true }> {
      const res = await deps.http.request<{ ok: true }>({
        method: 'POST', path: `/internal/admin/agent-operators/${encodeURIComponent(operatorId)}/agents/${encodeURIComponent(agentId)}/review`, body: { action, confirm_registry_tokens: confirmRegistryTokens },
      });
      if (res === undefined) throw new Error('admin_agent_operator_agent_review_returned_no_body');
      return res;
    },

    async revokeAgentOperatorKey(operatorId: string, keyId: string, reason: string): Promise<{ ok: true }> {
      const res = await deps.http.request<{ ok: true }>({
        method: 'POST', path: `/internal/admin/agent-operators/${encodeURIComponent(operatorId)}/keys/${encodeURIComponent(keyId)}/revoke`, body: { reason },
      });
      if (res === undefined) throw new Error('admin_agent_operator_key_revoke_returned_no_body');
      return res;
    },

    async inviteAgentOperator(email: string): Promise<AdminAgentOperatorInviteResponse> {
      const res = await deps.http.request<AdminAgentOperatorInviteResponse>({
        method: 'POST', path: '/internal/admin/agent-operators/invite', body: { email },
      });
      if (res === undefined) throw new Error('admin_agent_operator_invite_returned_no_body');
      return res;
    },

    async getAgentOperatorEnrollmentMode(): Promise<{ mode: 'open' | 'invite_only' }> {
      const res = await deps.http.request<{ mode: 'open' | 'invite_only' }>({
        method: 'GET', path: '/internal/admin/agent-operators/enrollment-mode',
      });
      if (res === undefined) throw new Error('admin_agent_operator_enrollment_mode_read_returned_no_body');
      return res;
    },
    async setAgentOperatorEnrollmentMode(mode): Promise<{ ok: true; mode: 'open' | 'invite_only' }> {
      const res = await deps.http.request<{ ok: true; mode: 'open' | 'invite_only' }>({
        method: 'POST', path: '/internal/admin/agent-operators/enrollment-mode', body: { mode },
      });
      if (res === undefined) throw new Error('admin_agent_operator_enrollment_mode_write_returned_no_body');
      return res;
    },

    async resolveTrustDispute(disputeId: string, outcome: 'upheld' | 'overturned', note: string): Promise<{ ok: true; status: string }> {
      const res = await deps.http.request<{ ok: true; status: string }>({
        method: 'POST',
        path: `/internal/admin/trust-disputes/${encodeURIComponent(disputeId)}/resolve`,
        body: { outcome, note },
      });
      if (res === undefined) throw new Error('admin_trust_dispute_resolve_returned_no_body');
      return res;
    },

    async getTrustEngine(opts: { readonly include_acked?: boolean } = {}): Promise<TrustEngineAdminResponse> {
      const res = await deps.http.request<TrustEngineAdminResponse>({
        method: 'GET',
        path: opts.include_acked === true ? '/internal/admin/trust-engine?include_acked=1' : '/internal/admin/trust-engine',
      });
      if (res === undefined) throw new Error('admin_trust_engine_returned_no_body');
      return res;
    },

    // ADR-0236 — go-live readiness per site / agent.
    async getTrustReadiness(): Promise<TrustReadinessResponse> {
      const res = await deps.http.request<TrustReadinessResponse>({ method: 'GET', path: '/internal/admin/trust-engine/readiness' });
      if (res === undefined) throw new Error('admin_trust_readiness_returned_no_body');
      return res;
    },

    // ADR-0236 — acknowledge a key-change alert (note required; staff-audited).
    async ackTrustKeyAlert(alertId: number, note: string): Promise<{ ok: true; alert_id: number }> {
      const res = await deps.http.request<{ ok: true; alert_id: number }>({
        method: 'POST',
        path: `/internal/admin/trust-engine/key-alerts/${encodeURIComponent(String(alertId))}/ack`,
        body: { note },
      });
      if (res === undefined) throw new Error('admin_trust_key_alert_ack_returned_no_body');
      return res;
    },

    async getAgentRegistry(): Promise<AdminAgentRegistryResponse> {
      const res = await deps.http.request<AdminAgentRegistryResponse>({
        method: 'GET',
        path: '/internal/admin/agent-registry',
      });
      if (res === undefined) throw new Error('admin_agent_registry_returned_no_body');
      return res;
    },

    async approveAgentProposal(
      proposalId: string,
      body: AdminAgentRegistryApproveBody,
    ): Promise<AdminAgentRegistryDecisionResponse> {
      const res = await deps.http.request<AdminAgentRegistryDecisionResponse>({
        method: 'POST',
        path: `/internal/admin/agent-registry/proposals/${encodeURIComponent(proposalId)}/approve`,
        body,
      });
      if (res === undefined) throw new Error('admin_agent_registry_approve_returned_no_body');
      return res;
    },

    async rejectAgentProposal(proposalId: string, reason: string): Promise<AdminAgentRegistryDecisionResponse> {
      const res = await deps.http.request<AdminAgentRegistryDecisionResponse>({
        method: 'POST',
        path: `/internal/admin/agent-registry/proposals/${encodeURIComponent(proposalId)}/reject`,
        body: { reason },
      });
      if (res === undefined) throw new Error('admin_agent_registry_reject_returned_no_body');
      return res;
    },

    async snoozeAgentProposal(proposalId: string, days: 7 | 30 | 90): Promise<AdminAgentRegistryDecisionResponse> {
      const res = await deps.http.request<AdminAgentRegistryDecisionResponse>({
        method: 'POST',
        path: `/internal/admin/agent-registry/proposals/${encodeURIComponent(proposalId)}/snooze`,
        body: { days },
      });
      if (res === undefined) throw new Error('admin_agent_registry_snooze_returned_no_body');
      return res;
    },

    async revokeAgentProposal(proposalId: string, reason: string): Promise<AdminAgentRegistryDecisionResponse> {
      const res = await deps.http.request<AdminAgentRegistryDecisionResponse>({
        method: 'POST',
        path: `/internal/admin/agent-registry/proposals/${encodeURIComponent(proposalId)}/revoke`,
        body: { reason },
      });
      if (res === undefined) throw new Error('admin_agent_registry_revoke_returned_no_body');
      return res;
    },

    async getHomeExperiment(): Promise<AdminHomeExperimentResponse> {
      const res = await deps.http.request<AdminHomeExperimentResponse>({
        method: 'GET',
        path: '/internal/admin/home-experiment',
      });
      if (res === undefined) throw new Error('admin_home_experiment_returned_no_body');
      return res;
    },

    async putHomeExperiment(body: AdminHomeExperimentPutBody): Promise<AdminHomeExperimentResponse> {
      const res = await deps.http.request<AdminHomeExperimentResponse>({
        method: 'PUT',
        path: '/internal/admin/home-experiment',
        body,
      });
      if (res === undefined) throw new Error('admin_home_experiment_put_returned_no_body');
      return res;
    },

    async getHomeExperimentEvents(): Promise<AdminHomeExperimentEventsResponse> {
      const res = await deps.http.request<AdminHomeExperimentEventsResponse>({
        method: 'GET',
        path: '/internal/admin/home-experiment/events',
      });
      if (res === undefined) throw new Error('admin_home_experiment_events_returned_no_body');
      return res;
    },

    async getMetricsCohorts(country?: string): Promise<AdminMetricsCohorts> {
      // Default is the back-compat "all-countries" grid; only attach
      // ?country when the caller explicitly asks for a single segment.
      const query: Record<string, string | number | undefined> = {};
      if (country !== undefined && country.length > 0 && country !== 'all') {
        query['country'] = country;
      }
      const res = await deps.http.request<AdminMetricsCohorts>({
        method: 'GET',
        path: '/internal/admin/metrics/cohorts',
        query,
      });
      if (res === undefined) throw new Error('admin_metrics_cohorts_returned_no_body');
      return res;
    },

    // --------------------------------------------------------------
    // ADR-0263 S1 — Plans v2 wire implementations.
    // --------------------------------------------------------------
    async putPlanFeatures(
      planId: string,
      body: AdminPlanFeaturesPutBody,
    ): Promise<AdminPlanFeaturesPutResponse> {
      const res = await deps.http.request<AdminPlanFeaturesPutResponse>({
        method: 'PUT',
        path: `/internal/admin/plans/${encodeURIComponent(planId)}/features`,
        body,
      });
      if (res === undefined) throw new Error('admin_put_plan_features_returned_no_body');
      return res;
    },

    async getAddons(): Promise<AdminPlanAddonsListResponse> {
      const res = await deps.http.request<AdminPlanAddonsListResponse>({
        method: 'GET',
        path: '/internal/admin/addons',
      });
      if (res === undefined) throw new Error('admin_list_addons_returned_no_body');
      return res;
    },

    async getAddon(addonId: string): Promise<AdminPlanAddonDetailResponse> {
      const res = await deps.http.request<AdminPlanAddonDetailResponse>({
        method: 'GET',
        path: `/internal/admin/addons/${encodeURIComponent(addonId)}`,
      });
      if (res === undefined) throw new Error('admin_get_addon_returned_no_body');
      return res;
    },

    async createAddon(
      body: AdminPlanAddonCreateBody,
    ): Promise<AdminPlanAddonCreateResponse> {
      const res = await deps.http.request<AdminPlanAddonCreateResponse>({
        method: 'POST',
        path: '/internal/admin/addons',
        body,
      });
      if (res === undefined) throw new Error('admin_create_addon_returned_no_body');
      return res;
    },

    async updateAddon(
      addonId: string,
      body: AdminPlanAddonUpdateBody,
    ): Promise<AdminPlanAddonUpdateResponse> {
      const res = await deps.http.request<AdminPlanAddonUpdateResponse>({
        method: 'PATCH',
        path: `/internal/admin/addons/${encodeURIComponent(addonId)}`,
        body,
      });
      if (res === undefined) throw new Error('admin_update_addon_returned_no_body');
      return res;
    },

    async putAddonPrices(
      addonId: string,
      body: AdminPlanAddonPricesPutBody,
    ): Promise<AdminPlanAddonPricesPutResponse> {
      const res = await deps.http.request<AdminPlanAddonPricesPutResponse>({
        method: 'PUT',
        path: `/internal/admin/addons/${encodeURIComponent(addonId)}/prices`,
        body,
      });
      if (res === undefined) {
        throw new Error('admin_put_addon_prices_returned_no_body');
      }
      return res;
    },

    async getAccountSitePlans(
      accountId: string,
    ): Promise<AdminAccountSitePlansResponse> {
      const res = await deps.http.request<AdminAccountSitePlansResponse>({
        method: 'GET',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/site-plans`,
      });
      if (res === undefined) {
        throw new Error('admin_account_site_plans_returned_no_body');
      }
      return res;
    },

    async getAccountFeaturesInUse(
      accountId: string,
    ): Promise<AdminAccountFeaturesInUseResponse> {
      const res = await deps.http.request<AdminAccountFeaturesInUseResponse>({
        method: 'GET',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/features-in-use`,
      });
      if (res === undefined) {
        throw new Error('admin_account_features_in_use_returned_no_body');
      }
      return res;
    },

    async putSitePlan(
      accountId: string,
      siteId: string,
      body: AdminSitePlanAssignBody,
    ): Promise<AdminSitePlanAssignResponse> {
      const res = await deps.http.request<AdminSitePlanAssignResponse>({
        method: 'PUT',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/sites/${encodeURIComponent(siteId)}/plan`,
        body,
      });
      if (res === undefined) throw new Error('admin_put_site_plan_returned_no_body');
      return res;
    },

    async getAccountAddons(
      accountId: string,
    ): Promise<AdminAccountAddonsResponse> {
      const res = await deps.http.request<AdminAccountAddonsResponse>({
        method: 'GET',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/addons`,
      });
      if (res === undefined) {
        throw new Error('admin_account_addons_returned_no_body');
      }
      return res;
    },

    async putAccountAddon(
      accountId: string,
      body: AdminAccountAddonUpdateBody,
    ): Promise<AdminAccountAddonUpdateResponse> {
      const res = await deps.http.request<AdminAccountAddonUpdateResponse>({
        method: 'PUT',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/addons`,
        body,
      });
      if (res === undefined) {
        throw new Error('admin_put_account_addon_returned_no_body');
      }
      return res;
    },

    async getAccountSiteUsage(
      accountId: string,
    ): Promise<AdminAccountSiteUsageResponse> {
      const res = await deps.http.request<AdminAccountSiteUsageResponse>({
        method: 'GET',
        path: `/internal/admin/accounts/${encodeURIComponent(accountId)}/site-usage`,
      });
      if (res === undefined) {
        throw new Error('admin_account_site_usage_returned_no_body');
      }
      return res;
    },
  };
}
